import React, { useState, useEffect, useContext } from 'react';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  OverlayTrigger,
  Row,
  Tab,
  Tooltip
} from 'react-bootstrap';
import useApi from 'hooks/useApi';
import {
  getDistricts,
  getMonitor,
  getTaluk,
  getVillage,
  getVillageLists
} from 'api/endpoints/SubProjectPedologicalServices';
import { getProjectDetails } from 'api/endpoints/ManagementServices';
import { isEmpty } from 'lodash';
import { get } from 'lodash';
import FalconCardHeader from 'components/common/FalconCardHeader';
import AppContext from 'context/Context';
import CustomSpinner from 'components/common/CustomSpinner';
import { toast } from 'react-toastify';
import CustomSelect from 'components/common/CustomSelect';
import { jwtDecode } from 'jwt-decode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import MultiYearMap from '../monitoring/MultiYearMap';
import SocChart from './Chart';
import CardSkeleton from 'components/common/skeleton/CardSkeleton';
import Select from 'react-select';
import DataTable from './DataTable';
import { getTenantFromToken } from 'utils/routeUtils';

const TableProject = () => {
  const {
    config: { isRTL, isDark }
  } = useContext(AppContext);

  const tenant = getTenantFromToken();
  const [loading, setLoading] = useState(false);
  const [monitorLoading, setMonitorLoading] = useState(false);
  const [villageLists, setVillageLists] = useState([]);
  const [lastSearchData, setLastSearchData] = useState({
    state: '',
    district: '',
    block: '',
    village: ''
  });

  const [villageName, setVillageName] = useState('');

  const [districtsList, setDistrictsList] = useState([]);
  const [state, setState] = useState('maharashtra');
  const [district, setDistrict] = useState(null);
  const [block, setBlock] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [pedologicalObservation, setPedologicalObservation] = useState({});
  const [blockList, setBlockList] = useState([]);
  const [villageList, setVillageList] = useState([]);
  const [village, setVillage] = useState([]);
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [coordinates, setCoordinates] = useState([]);
  const [ProjectDetails, setProjectDetails] = useState({});
  const [isSelected, setIsSelected] = useState(false);
  const [activeTab, setActiveTab] = useState('soc');

  const getDistrictsAPI = useApi(getDistricts);
  const getBlockAPI = useApi(getTaluk);
  const monitorAPI = useApi(getMonitor);
  const getVillageListAPI = useApi(getVillageLists);
  const getVillageAPI = useApi(getVillage);
  const getCoordinatesAPI = useApi(getProjectDetails);

  // Fetch districts on component mount
  useEffect(() => {
    setState('18');
  }, []);

  // Fetch districts on component mount
  useEffect(() => {
    if (state) {
      setDistrictsList([{ label: 'Loading.....', isDisabled: true }]);
      getDistrictsAPI.request({ state, tenant });
    }
  }, [state]);

  // Fetch blocks when district changes
  useEffect(() => {
    if (district) {
      setBlockList([{ label: 'Loading.....', isDisabled: true }]);
      getBlockAPI.request({ state, district: district.value });
    }
  }, [district]);

  // Fetch monitor and coordinates data when village changes
  // useEffect(() => {
  //   if (!isEmpty(villageID)) {
  //     monitorAPI.request(villageID);
  //     getCoordinatesAPI.request({ tenant, request_id: villageID });
  //   }
  // }, [villageID]);

  // Fetch villages when block changes
  useEffect(() => {
    if (district?.label && block?.label) {
      setVillageList([{ label: 'Loading.....', isDisabled: true }]);
      getVillageAPI.request({
        state,
        district: district.value,
        taluk: block.value
      });
    }
  }, [block]);

  // Update district list when districts API data changes
  useEffect(() => {
    const districts = get(getDistrictsAPI, 'data.results', []);
    const districtList = districts?.map(dist => ({
      value: dist.id,
      label: dist.name
    }));
    setDistrictsList(districtList);
  }, [getDistrictsAPI.data]);

  // Update block list when blocks API data changes
  useEffect(() => {
    const blocks = get(getBlockAPI, 'data.results', []);
    const blockList = blocks.map(block => ({
      value: block.id,
      label: block.name
    }));
    !isEmpty(blockList) && setBlockList(blockList);
  }, [getBlockAPI.data]);

  // Update village list when villages API data changes
  useEffect(() => {
    const villages = get(getVillageAPI, 'data.results', []);
    const villageList = villages?.map(village => ({
      value: village.id,
      label: village.name
    }));
    !isEmpty(villageList) && setVillageList(villageList);
  }, [getVillageAPI.data]);

  // Handle errors in fetching village lists
  useEffect(() => {
    if (!isEmpty(getVillageListAPI.error)) {
      setVillageList([]);
    }
  }, [getVillageListAPI.error]);

  // Handle search form submission
  const handleSearch = async event => {
    event.preventDefault();
    setLoading(true);
    setMonitorLoading(true);
    setIsSelected(true);
    setSelectedVillage(village[0]?.label);
    const villageIDs = village.map(option => option.value);

    if (!isEmpty(villageIDs) && isEmpty(state)) {
      setLoading(false);
      return;
    }

    try {
      const payload = { project_ids: villageIDs };
      const response = await getVillageLists(payload);
      setTableData(response.data.results.result);

      // Debugging getMonitor
      const monitorRequestId = village[0]?.value;
      const data = await getMonitor(monitorRequestId);

      // Debugging getProjectDetails
      const coordinates = await getProjectDetails({
        tenant,
        request_id: monitorRequestId
      });
      setProjectDetails(coordinates.data.results);
      setPedologicalObservation(data.data.results.pedological_observation);
    } catch (error) {
      if (error.response) {
        console.error('Error fetching data:', error.response.data);
      } else {
        console.error('Error fetching data:', error.message);
      }
    } finally {
      setLoading(false);
      setMonitorLoading(false);
    }
  };

  useEffect(() => {
    if (ProjectDetails && ProjectDetails.coordinates) {
      const isMultiPolygon =
        ProjectDetails.coordinates.includes('MULTIPOLYGON');

      if (isMultiPolygon) {
        const coordinatesRegex = /\(\((.*?)\)\)/g;
        const matches = ProjectDetails.coordinates.match(coordinatesRegex);

        if (matches) {
          const formattedCoordinates = matches.map(match => {
            const coordinatesArray = match.match(/-?\d+\.\d+/g).map(Number);

            const formatted = [];
            for (let i = 0; i < coordinatesArray.length; i += 2) {
              formatted.push([coordinatesArray[i + 1], coordinatesArray[i]]);
            }
            return formatted;
          });
          setCoordinates(formattedCoordinates);
        }
      } else {
        const coordinatesArray = ProjectDetails?.coordinates
          .match(/-?\d+\.\d+/g)
          .map(Number);
        const formattedCoordinates = [];
        for (let i = 0; i < coordinatesArray.length; i += 2) {
          formattedCoordinates.push([
            coordinatesArray[i + 1],
            coordinatesArray[i]
          ]);
        }
        setCoordinates(formattedCoordinates);
      }
    }
  }, [ProjectDetails]);

  const handleReload = () => {
    getVillageListAPI.request(lastSearchData);
  };

  // Event handler for village name click
  const handleVillageClick = async (village, district, block, id) => {
    setMonitorLoading(true);
    setSelectedVillage(village);
    try {
      const response = await getMonitor(id);
      setPedologicalObservation(response.data.results.pedological_observation);
      const coordinatesResponse = await getProjectDetails({
        tenant,
        request_id: id
      });
      setProjectDetails(coordinatesResponse.data.results);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Handle error scenarios here
    } finally {
      setMonitorLoading(false);
    }
  };

  const customStyles = {
    control: provided => ({
      ...provided,
      // overflowY: 'scroll',
      // whiteSpace: 'nowrap',
      // textOverflow: 'ellipsis',
      // height: '40px',
      minHeight: '80px',
      backgroundColor: 'blue'
    }),
    valueContainer: provided => ({
      ...provided,
      whiteSpace: 'nowrap',
      overflowY: 'auto',
      textOverflow: 'ellipsis',
      // maxWidth: 'calc(100% - 8px)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      maxWidth: '200px',
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignContent: 'flex-start',
      // background: 'red',
      maxHeight: '90px'
    }),

    multiValue: provided => ({
      ...provided,
      // display: 'none',
      backgroundColor: 'yellow',
      height: 'fit-content'
    }),
    multiValueLabel: provided => ({
      ...provided
      // display: 'none'
      // backgroundColor: 'green',
      // height: '20px'
    })
  };
  const customOptions = [
    { value: 'select-all', label: 'Select All' },
    ...villageList
  ];

  const handleSelectAll = () => {
    if (village.length === villageList.length) {
      setVillage([]);
    } else {
      setVillage(villageList);
    }
  };

  const handleSelectChange = selected => {
    if (selected && selected.some(opt => opt.value === 'select-all')) {
      handleSelectAll();
    } else {
      setVillage(selected);
    }
  };

  return (
    <Container fluid className="p-0">
      <Row className="mb-3">
        <Col xs={12}>
          <Form onSubmit={handleSearch} className="mt-1">
            <Row className="g-3 g-lg-4">
              <Col lg={3}>
                <CustomSelect
                  // components={{ MultiValueContainer }}
                  options={districtsList}
                  isClearable={true}
                  placeholder="Select District"
                  classNamePrefix="react-select"
                  value={district}
                  onChange={value => {
                    setDistrict(value);
                    setBlock(null);
                    setVillage([]);
                    setPedologicalObservation({});
                    setTableData([]);
                    setIsSelected(false);
                  }}
                />
              </Col>
              <Col lg={3}>
                <CustomSelect
                  options={blockList}
                  isClearable={true}
                  isDisabled={isEmpty(district)}
                  placeholder="Select Block"
                  classNamePrefix="react-select"
                  value={block}
                  onChange={value => {
                    setBlock(value);
                    setVillage([]);
                    setPedologicalObservation({});
                    setTableData([]);
                    setIsSelected(false);
                  }}
                />
              </Col>
              <Col lg={3}>
                <Select
                  options={customOptions}
                  isClearable={true}
                  isDisabled={isEmpty(district) || isEmpty(block)}
                  placeholder="Select Village"
                  classNamePrefix="react-select"
                  value={village}
                  onChange={handleSelectChange}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  styles={customStyles}
                />
              </Col>
              {/* <Col lg={3}>
                <Form.Control
                  type="text"
                  disabled={isEmpty(district) || isEmpty(block)}
                  className={!isDark && 'bg-white'}
                  placeholder="Village Name"
                  value={villageName}
                  onChange={e => setVillageName(e.target.value)}
                />
              </Col> */}
              <Col className="d-flex justify-content-end align-items-start">
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      {!district || !village || !isSelected ? (
        <Container
          className="d-flex align-items-center justify-content-center"
          style={{ height: '200px' }}
        >
          <p
            style={{
              color: 'green',
              fontSize: '20px',
              textAlign: 'center'
            }}
          >
            Please select village and click ‘Search’
          </p>
        </Container>
      ) : (
        <>
          {isEmpty(tableData) && !loading ? (
            <p className="text-center">No Village Data Found</p>
          ) : (
            <Row className="mb-3">
              <div style={styles.tabHeader}>
                <button
                  style={
                    activeTab == 'soc'
                      ? { ...styles.tabButton, ...styles.tabButtonActive }
                      : styles.tabButton
                  }
                  onClick={() => setActiveTab('soc')}
                >
                  Soil Organic Carbon
                </button>
                <button
                  style={
                    activeTab == 'water'
                      ? { ...styles.tabButton, ...styles.tabButtonActive }
                      : styles.tabButton
                  }
                  onClick={() => setActiveTab('water')}
                >
                  Soil Water Retention Capacity
                </button>
              </div>
              <Col>
                <Row>
                  <Col className=" pb-3"></Col>
                </Row>
                {loading ? (
                  <CustomSpinner />
                ) : (
                  <>
                    <DataTable
                      data={tableData}
                      activeTab={activeTab}
                      handleClick={handleVillageClick}
                      description={{
                        socDesc: pedologicalObservation?.soc_chart?.description,
                        swrcDesc:
                          pedologicalObservation
                            ?.soil_water_retention_capacity_chart?.description
                      }}
                    />
                  </>
                )}
              </Col>
            </Row>
          )}
          {loading || monitorLoading ? (
            <CardSkeleton footer={false} />
          ) : activeTab == 'soc' ? (
            <>
              <Row>
                {pedologicalObservation?.soc_chart && (
                  <Col>
                    <Card>
                      <SocChart data={pedologicalObservation?.soc_chart} />
                    </Card>
                  </Col>
                )}
              </Row>
              <Row className="mt-3">
                {pedologicalObservation?.soc_map && (
                  <Col>
                    <Card>
                      <MultiYearMap
                        data={pedologicalObservation?.soc_map.data}
                        coordinates={coordinates}
                        legendData={
                          pedologicalObservation?.soc_map?.Legend ||
                          pedologicalObservation?.soc_map?.legend
                        }
                        variant={pedologicalObservation?.soc_map?.variant}
                        label={pedologicalObservation?.soc_map?.label}
                      />
                    </Card>
                  </Col>
                )}
              </Row>
            </>
          ) : (
            <></>
          )}
        </>
      )}
    </Container>
  );
};

export default TableProject;

const styles = {
  tabContainer: {
    width: '100%',
    maxWidth: '600px',
    margin: '2rem auto',
    fontFamily: 'Arial, sans-serif'
  },

  tabHeader: {
    display: 'flex',
    borderBottom: '2px solid #bbcebf'
  },

  tabButton: {
    padding: '10px 40px',
    fontSize: '16px',
    backgroundColor: '#f0f0f0',
    border: 'none',
    borderRadius: '5px 5px 0 0',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    color: '#4e6a54',
    fontWeight: '500'
  },

  tabButtonHover: {
    backgroundColor: '#e0e0e0'
  },

  tabButtonActive: {
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderBottom: 'none'
  },

  tabContent: {
    padding: '20px',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderTop: 'none'
  },

  tabContentH2: {
    marginTop: 0,
    color: '#4e6a54'
  },

  tabContentP: {
    color: '#666'
  }
};
